import React, {useState, useEffect, useCallback, useRef} from 'react';
import {isHiddenAccount, stationDisplayName} from '@embertracking/common';
import {BrowserRouter} from 'react-router-dom';
import {onSnapshot, collection, doc} from 'firebase/firestore';
import {onAuthStateChanged, signOut} from 'firebase/auth';
import {SnackbarProvider, closeSnackbar, MaterialDesignContent} from 'notistack';
import {LicenseInfo} from '@mui/x-license';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import sortBy from 'lodash/sortBy';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import {setUserId} from 'firebase/analytics';
import {ConfirmProvider} from 'material-ui-confirm';
import CloseIcon from '@mui/icons-material/Close';
import {consoleLoggingIntegration, setUser as sentrySetUser, addBreadcrumb, captureMessage} from '@sentry/react';
import moment from 'moment';
import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {StationsContext} from '-/contexts/Stations';
import {ApparatusContext} from '-/contexts/Apparatus';
import {GearLocationsProvider} from '-/contexts/GearLocations';
import {FuelContext} from '-/contexts/Fuel';
import {FileCategoriesProvider} from '-/contexts/FileCategories';
import {HeaderSlotProvider} from '-/contexts/HeaderSlot';
import {db, auth, analytics, sentryDsn, projectId} from '-/firebase.js';
import {setFeatureSettings} from '-/features';
import Theme from '@embertracking/ui/style/Theme';
import AppContainer from '-/components/AppContainer';
import Router from '-/Router';
import {BOOTSTRAP_WATCHDOG_MS, BootstrapScreen, ErrorBoundary} from '@embertracking/ui/components';
import {initSentry} from '@embertracking/ui/data';
import {callFunction, hasPermission, processRawDoc, processRawDocs, processUserName} from '-/data/utils';
import {setupGlobalErrorHandlers, isOfflineError, reportError, resilientSnapshot} from '-/data/errorReporting';
import {clearSession, expireSession} from '-/data/session';
import {HUB_SESSION_EXPIRED_CODE, HUB_SESSION_ENDED_MESSAGE} from '-/data/hubSession';
import {readSharedCache, readUserCache, writeSharedCache, writeUserCache, reviveDates} from '-/data/bootstrapCache';
import useSessionTimeout from '-/hooks/useSessionTimeout';
import IconButton from '@mui/material/IconButton';

moment.updateLocale('en', {
    calendar: {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'dddd',
        lastDay: '[Yesterday]',
        lastWeek: '[Last] dddd',
        sameElse: function() {
            return '[' + this.fromNow() + ']';
        }
    }
});





const DismissableSnackbar = React.forwardRef((props, ref) => (
    <div onClick={() => closeSnackbar(props.id)} style={{cursor: 'pointer'}}>
        <MaterialDesignContent {...props} ref={ref} />
    </div>
));

LicenseInfo.setLicenseKey('ead34a754e16dcf7cab340a0bb09e760Tz0xMjY4NjgsRT0xODA0OTgyMzk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTEsS1Y9Mg==');

// Shared Sentry config (tracing, chunk-load filter, the cross-app ignoreErrors
// noise list) lives in @embertracking/ui/data/initSentry. Only the
// embertracking-specific bits are passed here.
initSentry({
    dsn: sentryDsn,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    environment: 'production',
    enableLogs: true,
    integrations: [
        // Send console.warn and console.error calls as logs to Sentry. 'log' is
        // intentionally excluded: it's a side channel that could leak PII (#555).
        // Intentional structured logs should use Sentry.logger.info directly.
        consoleLoggingIntegration({levels: ['warn', 'error']})
    ],
    // Drop expected network/offline errors (Firestore offline, axios Network
    // Error). Browser-API instrumentation in Sentry captures some of these
    // before our handlers run.
    shouldDropEvent: error => isOfflineError(error)
});

// Backstop only. Cold start now goes through the /bootstrap endpoint (one bounded
// request) plus a localStorage first-paint cache, so the racing Firestore listen
// channels that used to hang on Safari no longer gate the loading screen. If the
// app is somehow still initializing this long after mount, show a retry screen
// (not a silent reload) and report which flag is stuck.
const CURRENT_USER_VOLATILE_FIELDS = ['lastActiveAt'];

const currentUserHasMeaningfulChange = (prev, next) => {
    if (!prev || prev.uid !== next?.uid) {
        return true;
    }

    return !isEqual(
        omit(prev, CURRENT_USER_VOLATILE_FIELDS),
        omit(next, CURRENT_USER_VOLATILE_FIELDS)
    );
};

export default function App() {
    // Hydrate the shared (non-PII) slice — settings + stations — synchronously
    // from localStorage so the app chrome paints on reload with zero network. The
    // per-user slice (currentUser/permissions) is NOT read here: it's only safe to
    // paint once the Auth SDK has confirmed which user is signed in (see
    // bootstrapUser), so we never flash the previous user on a shared device.
    const initialSharedRef = useRef(undefined);
    if (initialSharedRef.current === undefined) {
        initialSharedRef.current = readSharedCache(projectId);
    }
    const initialShared = initialSharedRef.current;

    const [initializingAuth, setInitializingAuth] = useState(true);
    const [initializingSettings, setInitializingSettings] = useState(!initialShared);
    const [initializingStations, setInitializingStations] = useState(!initialShared);
    const [bootstrapError, setBootstrapError] = useState(false);
    const [currentUser, setCurrentUser] = useState();
    const [member, setMember] = useState();
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState(false);
    const [usersFromCache, setUsersFromCache] = useState(true);
    const [settings, setSettings] = useState(initialShared?.settings ?? null);
    const [stationsLoading, setStationsLoading] = useState(true);
    const [stationsError, setStationsError] = useState(false);
    const [stations, setStations] = useState(initialShared?.stations ?? null);
    const [apparatus, setApparatus] = useState([]);
    const [loadingApparatus, setLoadingApparatus] = useState(true);
    const [apparatusError, setApparatusError] = useState(false);
    const [apparatusFromCache, setApparatusFromCache] = useState(true);
    const [fuelTanks, setFuelTanks] = useState([]);
    const [loadingFuel, setLoadingFuel] = useState(true);
    // Monotonic id so a slow /bootstrap that resolves after the user has already
    // signed out or switched accounts can't clobber the newer state.
    const authAttemptRef = useRef(0);

    // Breadcrumb each bootstrap milestone the first time it happens, so a
    // watchdog report (or any later error) carries a timeline showing which
    // listener never reported. Fire-once via a ref so repeat snapshots don't
    // flood the breadcrumb buffer.
    const milestonesRef = useRef({settings: false, stations: false, auth: false, users: false});
    const markMilestone = useCallback(name => {
        if (milestonesRef.current[name]) {
            return;
        }
        milestonesRef.current[name] = true;
        addBreadcrumb({category: 'bootstrap', level: 'info', message: `bootstrap: ${name} ready`});
    }, []);

    const fetchSettings = useCallback(() => {
        const ref = doc(db, 'settings', 'general');
        return resilientSnapshot(
            errorHandler => onSnapshot(
                ref,
                snapshot => {
                    const row = processRawDoc(snapshot);
                    setSettings(row || {});
                    setFeatureSettings(row || {});
                    markMilestone('settings');
                    setInitializingSettings(false);
                },
                errorHandler
            ),
            'bootstrap:settings',
            // Don't strand initializingSettings=true forever if the listener
            // errors — the loading screen is gated on it, so a silent error
            // here is an indefinite hang (matches apparatus/fuel handling).
            () => {
                addBreadcrumb({category: 'bootstrap', level: 'error', message: 'bootstrap: settings snapshot error'});
                setInitializingSettings(false);
            }
        );
    }, [markMilestone]);

    const fetchStations = useCallback(() => {
        const ref = collection(db, 'stations');
        return resilientSnapshot(
            errorHandler => onSnapshot(
                ref,
                {includeMetadataChanges: true},
                snapshot => {
                    // Wait for the first server snapshot before routing by hall
                    // count; later offline snapshots must not unmount an editor.
                    setStationsLoading(wasLoading => wasLoading && Boolean(snapshot.metadata?.fromCache));
                    setStationsError(false);
                    const stationsData = processRawDocs(snapshot);
                    setStations(sortBy(stationsData, stationDisplayName));
                    markMilestone('stations');
                    setInitializingStations(false);
                },
                errorHandler
            ),
            'bootstrap:stations',
            // Don't strand initializingStations=true forever if the listener
            // errors — the loading screen is gated on it.
            () => {
                setStationsLoading(false);
                setStationsError(true);
                addBreadcrumb({category: 'bootstrap', level: 'error', message: 'bootstrap: stations snapshot error'});
                setInitializingStations(false);
            }
        );
    }, [markMilestone]);

    const fetchApparatus = useCallback(() => {
        const ref = collection(db, 'apparatus');
        // includeMetadataChanges so we get the follow-up emission when the server
        // confirms the snapshot (fromCache flips true→false) even if the doc set
        // didn't change — that transition is how we distinguish a real empty fleet
        // from a cache-only/offline empty.
        return resilientSnapshot(
            errorHandler => onSnapshot(
                ref,
                {includeMetadataChanges: true},
                snapshot => {
                    setApparatus(processRawDocs(snapshot));
                    setApparatusFromCache(snapshot.metadata.fromCache);
                    setApparatusError(false);
                    setLoadingApparatus(false);
                },
                errorHandler
            ),
            'apparatus',
            // If the listener errors (e.g. firing before auth is restored on
            // first emulator start), don't leave `loadingApparatus=true` forever
            // — anything downstream (gear items grid) reads it as a global
            // loading flag and gets stuck.
            () => {
                setApparatusError(true);
                setLoadingApparatus(false);
            }
        );
    }, []);

    // settings/general + stations are no longer world-readable (#1464 — the
    // rules leaked every station's address and the whole tenant config to anyone
    // with the public web apiKey), so these listeners can only attach once
    // signed in. Keyed on `currentUser?.uid` rather than `currentUser` on
    // purpose: the object identity changes on every self-profile snapshot and
    // permissions refresh, and re-subscribing both channels on each of those
    // would be a real regression. Mirrors the roster effect below.
    useEffect(() => {
        if (!currentUser?.uid) return undefined;

        const unsubscribeSettings = fetchSettings();
        setStationsLoading(true);
        setStationsError(false);
        const unsubscribeStations = fetchStations();

        return () => {
            unsubscribeSettings();
            unsubscribeStations();
        };
    }, [currentUser?.uid, fetchSettings, fetchStations]);

    // Pre-auth branding + the station-login picker, from the api's public
    // endpoint instead of the (now denied) direct Firestore reads. Progressive
    // enhancement: the login screen paints immediately on auth resolution and
    // this fills in the department name/logo and the station list when it lands;
    // a failure just leaves the generic Ember Tracking screen.
    useEffect(() => {
        let cancelled = false;

        callFunction('publicConfig').then(data => {
            // A response that arrives after sign-in must never clobber the
            // authoritative /bootstrap state with its allowlisted subset.
            if (cancelled || auth.currentUser) return;

            setSettings(prev => ({...(prev || {}), ...(data?.settings || {})}));
            setStations(sortBy(data?.stations || [], stationDisplayName));
            // Deliberately NOT setFeatureSettings(): every hasFeature call site
            // is inside Router's `currentUser && (…)` branch, so pre-auth flags
            // are never read — pushing this subset into the module-level
            // settings would only create a window where real flags read false.
            // Same reason /publicConfig never writes the shared first-paint
            // cache: caching the subset would poison the next cold paint of the
            // authed shell (missing formPacks/fuelAlerts/hub keys).
            markMilestone('settings');
            markMilestone('stations');
            setInitializingSettings(false);
            setInitializingStations(false);
        }).catch(() => {
            if (cancelled) return;
            addBreadcrumb({category: 'bootstrap', level: 'error', message: 'bootstrap: publicConfig failed'});
            setInitializingSettings(false);
            setInitializingStations(false);
        });

        return () => {
            cancelled = true;
        };
    }, [markMilestone]);

    // Apparatus requires auth, so only subscribe once currentUser is set.
    // Subscribing on initial mount races onAuthStateChanged and hits permission-denied,
    // which puts the listener in a dead state until the next page load.
    // Fuel tanks normalize fuelType/currentLevel/currentMeter to the
    // type/level/meter the UI reads, and keep Firestore Timestamps as moments
    // (via processRawDocs) so the cards can show "x ago" from lastEntryAt.
    const fetchFuelTanks = useCallback(() => {
        const ref = collection(db, 'fuelTanks');
        const unsubscribe = onSnapshot(
            ref,
            snapshot => {
                const docs = processRawDocs(snapshot).map(t => ({...t, type: t.fuelType, level: t.currentLevel ?? null, meter: t.currentMeter ?? null}));
                setFuelTanks(sortBy(docs, ['station', 'name']));
                setLoadingFuel(false);
            },
            error => {
                console.warn('Fuel tanks snapshot error:', error);
                setLoadingFuel(false);
            }
        );
        return unsubscribe;
    }, []);

    // Apparatus is fleet reference data any signed-in member can read (see the
    // /apparatus rule) — every page that renders an incident, deployment or gear
    // placement needs the tags — so it's gated on auth alone.
    //
    // Fuel is NOT: `fuelTanks` requires fuel.read. `DefaultPermissions` grants it
    // on the CLIENT, but the rules only see the custom claim, where a restricted
    // member (a payroll-only role) carries an explicit `fu.r: false`. Subscribing
    // regardless meant a guaranteed permission-denied on every page load
    // (NORTHSHOREFD-6970A-6). Mirrors FileCategoriesProvider.
    useEffect(() => {
        if (!currentUser) return undefined;

        const canReadFuel = hasPermission(currentUser, 'fuel.read');

        // Terminal empty state, not an error: there are no tanks to load for this
        // member, and the fuel card's spinner is gated on `loadingFuel`.
        if (!canReadFuel) {
            setFuelTanks([]);
            setLoadingFuel(false);
        }

        const unsubscribeApparatus = fetchApparatus();
        const unsubscribeFuel = canReadFuel ? fetchFuelTanks() : undefined;
        return () => {
            unsubscribeApparatus();
            unsubscribeFuel?.();
        };
    }, [currentUser, fetchApparatus, fetchFuelTanks]);

    // Cold-start bootstrap. Instead of a forced ID-token refresh plus three racing
    // Firestore listen channels, we call the /bootstrap endpoint once: it verifies
    // the (cached) token server-side, returns settings + stations + the caller's
    // currentUser + authoritative permissions, and signals signOut/mustReauth. The
    // roster and live updates are attached AFTER first paint (see the effects
    // below), so a stalled Safari long-poll can no longer strand the spinner.
    const bootstrapUser = useCallback(async user => {
        const attempt = authAttemptRef.current + 1;
        authAttemptRef.current = attempt;
        const isStale = () => authAttemptRef.current !== attempt;

        if (!user) {
            // Signed out — clearSession also wipes the per-user first-paint cache.
            clearSession();
            sentrySetUser(null);
            setCurrentUser(undefined);
            setUsers([]);
            setInitializingAuth(false);
            // Nothing else clears these for a signed-out user now that the
            // settings/stations listeners are auth-gated (#1464), and the
            // loading screen is gated on all three. The login screen must paint
            // on auth resolution alone — with zero network dependency — so a
            // slow or dead api can't strand it behind a spinner. /publicConfig
            // fills the branding in afterwards.
            setInitializingSettings(false);
            setInitializingStations(false);
            return;
        }

        const {uid} = user;

        // Optimistic paint from cache — only now that the Auth SDK has confirmed
        // THIS uid. A different account (or a cleared cache) misses, so we never
        // flash the previous user on a shared device.
        const cached = readUserCache(projectId, uid);
        if (cached && !isStale()) {
            setCurrentUser({...cached.currentUser, permissions: cached.permissions});
            markMilestone('auth');
            setInitializingAuth(false);
        }

        // callFunction owns reporting for the request itself (#2050): it has
        // already captured an unexpected 5xx and deliberately drops expected
        // 4xx/offline failures. This catch reports only what fails after the
        // response arrives (the token refresh, applying the payload).
        let responded = false;
        try {
            const data = await callFunction('bootstrap');
            responded = true;
            if (isStale()) return;

            if (data.signOut) {
                // Disabled / revoked / archived server-side — eject.
                await signOut(auth);
                return;
            }
            if (data.mustReauth) {
                // Claims changed server-side; force-refresh so the token Firestore
                // rules read is re-synced. Only fires when it actually differs.
                await user.getIdTokenResult(true);
                if (isStale()) return;
            }

            const nextSettings = reviveDates(data.settings) || {};
            const nextStations = sortBy(reviveDates(data.stations) || [], stationDisplayName);
            const nextCurrentUser = {...reviveDates(data.currentUser), permissions: data.permissions};

            setSettings(nextSettings);
            setFeatureSettings(nextSettings);
            setStations(nextStations);
            setCurrentUser(prev => (currentUserHasMeaningfulChange(prev, nextCurrentUser) ? nextCurrentUser : prev));

            if (analytics) {
                setUserId(analytics, uid);
            }
            sentrySetUser({id: uid});

            const lastSignIn = user.metadata?.lastLoginAt || user.metadata?.lastSignInTime;
            const storageKey = `loginLogged_${uid}`;
            if (lastSignIn && lastSignIn !== localStorage.getItem(storageKey) && !isHiddenAccount(data.currentUser, uid)) {
                localStorage.setItem(storageKey, lastSignIn);
                // Login is the one history entry written outside the Admin SDK;
                // route it through the api so history rules can deny direct client
                // creates (#992). The localStorage guard is a call-reduction hint;
                // the server dedupes authoritatively.
                callFunction('recordLogin', {}).catch(() => {});
            }

            markMilestone('settings');
            markMilestone('stations');
            markMilestone('auth');
            setInitializingSettings(false);
            setInitializingStations(false);
            setBootstrapError(false);

            // Persist for an instant next paint. Raw (unrevived) payload is stored;
            // writeUserCache skips station accounts.
            writeSharedCache(projectId, {settings: data.settings, stations: data.stations});
            writeUserCache(projectId, uid, {currentUser: data.currentUser, permissions: data.permissions});
        } catch (e) {
            if (isStale()) return;
            // callFunction already signs out confirmed revoked/disabled sessions.
            if (e?.serverCode === 'session-ended') return;
            // An operator session past its hard end time: the api rejects every
            // call with this code, so a retry screen would spin forever. Sign out
            // the way the session timeout does, so the login page can say why.
            if (e?.serverCode === 'operator-session-expired') {
                await expireSession('Your operator session has ended.');
                return;
            }
            if (e?.serverCode === HUB_SESSION_EXPIRED_CODE) {
                await expireSession(HUB_SESSION_ENDED_MESSAGE);
                return;
            }
            if (responded) reportError(e, {context: 'bootstrap'});
            // If we already painted from cache, keep the working app up and let the
            // background listeners revalidate. With no cache there's nothing to
            // show, so surface the retry screen (watchdog is the last-resort
            // backstop if this somehow never resolves).
            if (!cached) setBootstrapError(true);
        } finally {
            if (!isStale()) setInitializingAuth(false);
        }
    }, [markMilestone]);

    useSessionTimeout(currentUser);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, bootstrapUser);
        return () => { authAttemptRef.current++; unsubscribeAuth(); };
    }, [bootstrapUser]);

    // Reflect the cache-hydrated settings into feature flags on mount (the live
    // settings snapshot / bootstrap will re-apply the authoritative version).
    useEffect(() => {
        if (initialShared?.settings) {
            setFeatureSettings(initialShared.settings);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Roster + liveness. Attached only once an authenticated user exists, AFTER
    // first paint — so a stalled usersLight long-poll no longer blocks the loading
    // screen. Also carries the archived signal: a member archived mid-session is
    // signed out here.
    useEffect(() => {
        if (!currentUser?.uid) return undefined;
        setUsersLoading(true);
        const ref = collection(db, 'usersLight');
        // includeMetadataChanges: see fetchApparatus — we need the server-confirmed
        // emission (fromCache → false) to trust an empty roster over a cache-only one.
        const unsubscribe = resilientSnapshot(
            errorHandler => onSnapshot(ref, {includeMetadataChanges: true}, async snapshot => {
                let newUsers = processRawDocs(snapshot);
                newUsers = newUsers.map(u => processUserName(u, {users: newUsers}));

                const mine = newUsers.find(doc => doc.uid === currentUser.uid);
                if (mine) {
                    if (mine.archived && !currentUser.isStation) {
                        await signOut(auth);
                        return;
                    }
                    // #1000: merge the slim roster projection OVER the existing user
                    // rather than replacing it, so self-PII (phone, dob, …) sourced from
                    // /bootstrap and the self users/{uid} listener below is not stripped
                    // when the slimmed usersLight snapshot fires.
                    setCurrentUser(prev => {
                        const next = {...prev, ...mine, permissions: currentUser.permissions};
                        return currentUserHasMeaningfulChange(prev, next) ? next : prev;
                    });
                }

                setUsers(newUsers.filter(u => !isHiddenAccount(u)));
                setUsersFromCache(snapshot.metadata.fromCache);
                setUsersError(false);
                setUsersLoading(false);
            }, errorHandler),
            'usersLight',
            () => {
                // resilientSnapshot swallows the sign-out/token-expiry race
                // (permission-denied while auth.currentUser is null) and retries
                // the wake-from-sleep expired-token denial once — this global
                // listener is attached on every page, so it was the loudest
                // source of both kinds of noise (JRFD-LOG-P/Z).
                addBreadcrumb({category: 'bootstrap', level: 'error', message: 'usersLight snapshot error (post-paint)'});
                setUsersError(true);
                setUsersLoading(false);
            }
        );
        return () => unsubscribe();
    }, [currentUser?.uid, currentUser?.isStation, currentUser?.permissions]);

    // #1000: the slimmed usersLight roster no longer carries the current user's own
    // PII (phone, dob, employeeId, …), but several self-service surfaces need it
    // (MFABanner reads currentUser.phone; the self-profile form resets from
    // currentUser). Source the full self profile from users/{uid} directly — the
    // rules allow a member with a live session to read their own doc. Station
    // logins have no users/{uid} doc, so skip them. Like the roster listener,
    // recover from an expired token on wake instead of leaving self updates
    // stopped until reload (EAGLEVALLEYRESCUE-6).
    useEffect(() => {
        if (!currentUser?.uid || currentUser.isStation) return undefined;
        const unsubscribe = resilientSnapshot(
            errorHandler => onSnapshot(doc(db, 'users', currentUser.uid), snapshot => {
                if (!snapshot.exists()) return;
                const self = processRawDoc(snapshot);
                setCurrentUser(prev => {
                    if (!prev) return prev;
                    const next = {...prev, ...self, permissions: prev.permissions};
                    return currentUserHasMeaningfulChange(prev, next) ? next : prev;
                });
            }, errorHandler),
            'currentUserSelf'
        );
        return () => unsubscribe();
    }, [currentUser?.uid, currentUser?.isStation]);

    // Setup global error handlers on mount
    useEffect(() => {
        setupGlobalErrorHandlers();
    }, []);

    const initializing = initializingAuth || initializingSettings || initializingStations;

    // Bootstrap watchdog: read the latest flags via a ref so the mount-time
    // timer reports whatever is still stuck when it fires.
    const initStateRef = useRef(null);
    initStateRef.current = {initializingAuth, initializingSettings, initializingStations};
    useEffect(() => {
        const timer = setTimeout(() => {
            const stalled = initStateRef.current;
            if (!(stalled.initializingAuth || stalled.initializingSettings || stalled.initializingStations)) {
                return;
            }

            // Cold start should complete via /bootstrap (one request) or the
            // first-paint cache well before this — reaching here means something is
            // genuinely wedged. Report it and show a retry screen instead of
            // silently reloading (the /bootstrap error path already handles the
            // common failure; this is the last-resort backstop for a true hang).
            addBreadcrumb({category: 'bootstrap', level: 'error', message: 'bootstrap watchdog fired', data: stalled});
            captureMessage('App stuck on loading screen', {
                level: 'error',
                tags: {bootstrap_stall: true},
                extra: {...stalled, milestones: milestonesRef.current, watchdogMs: BOOTSTRAP_WATCHDOG_MS}
            });
            setBootstrapError(true);
        }, BOOTSTRAP_WATCHDOG_MS);
        return () => clearTimeout(timer);
    }, []);

    if (bootstrapError) {
        return <BootstrapScreen
            title="We couldn't finish loading"
            message="Something went wrong while starting up. Please try again."
            onRetry={() => window.location.reload()}
        />;
    }

    if (initializing) {
        return <BootstrapScreen />;
    }

    const confirmProviderDefaultOptions = {
        confirmationButtonProps: {
            variant: 'contained',
            color: 'error'
        },
        contentProps: {
            dividers: true,
            sx: {
                pt: 2
            }
        }
    };

    return (
        <ErrorBoundary>
            <SettingsContext.Provider value={settings || {}}>
                <UserContext.Provider value={{currentUser, setCurrentUser, member, setMember, users, usersLoading, usersError, usersFromCache}}>
                    <StationsContext.Provider value={{stations, loading: stationsLoading, error: stationsError}}>
                        <ApparatusContext.Provider value={{apparatus, loading: loadingApparatus, error: apparatusError, fromCache: apparatusFromCache}}>
                            <GearLocationsProvider>
                                <FuelContext.Provider value={{tanks: fuelTanks, loading: loadingFuel}}>
                                    <FileCategoriesProvider>
                                        <Theme>
                                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-ca">
                                                <ConfirmProvider defaultOptions={confirmProviderDefaultOptions}>
                                                    <SnackbarProvider maxSnack={3} anchorOrigin={{horizontal: 'left', vertical: 'bottom'}} action={key => <IconButton size="small" color="inherit" onClick={() => closeSnackbar(key)}><CloseIcon fontSize="small" /></IconButton>} Components={{success: DismissableSnackbar, error: DismissableSnackbar, warning: DismissableSnackbar, info: DismissableSnackbar}}>
                                                        <BrowserRouter>
                                                            <HeaderSlotProvider>
                                                                <AppContainer>
                                                                    <Router />
                                                                </AppContainer>
                                                            </HeaderSlotProvider>
                                                        </BrowserRouter>
                                                    </SnackbarProvider>
                                                </ConfirmProvider>
                                            </LocalizationProvider>
                                        </Theme>
                                    </FileCategoriesProvider>
                                </FuelContext.Provider>
                            </GearLocationsProvider>
                        </ApparatusContext.Provider>
                    </StationsContext.Provider>
                </UserContext.Provider>
            </SettingsContext.Provider>
        </ErrorBoundary>
    );
}
